// Replaces the native Shopify checkout button in the cart with PayPal's
// guest card checkout (no PayPal account required), charging the exact
// Shopify cart total via a Draft Order.
(function () {
  var API_BASE = "https://paypal-shopify-checkout-production.up.railway.app";

  // Shopify's own "Buy it now" / accelerated checkout button on product
  // pages goes straight to native checkout, bypassing our PayPal flow
  // entirely (native checkout has no payment provider configured, so it
  // shows "This store can't accept payments"). Hide it everywhere — the
  // customer still has "Agregar al carrito", which does go through PayPal.
  var hideDynamicCheckoutStyle = document.createElement("style");
  hideDynamicCheckoutStyle.textContent =
    ".shopify-payment-button, .shopify-payment-button__wrapper { display: none !important; }";
  document.head.appendChild(hideDynamicCheckoutStyle);

  // This theme (Horizon) hides the header on scroll by fading it to
  // opacity:0 whenever its own sticky-tracking attribute goes "idle" (its
  // <header-component sticky="scroll-up"> only shows the header again once
  // you scroll back up). We want the cart icon visible at all times, so
  // force it sticky + fully opaque regardless of that state.
  var alwaysShowHeaderStyle = document.createElement("style");
  alwaysShowHeaderStyle.textContent =
    ".header-section { position: sticky !important; top: -1px !important; z-index: var(--layer-sticky, 9999) !important; }" +
    "#header-group { display: contents !important; }" +
    ".header[data-sticky-state] { opacity: 1 !important; }";
  document.head.appendChild(alwaysShowHeaderStyle);

  var sdkInstancePromise = null;
  var configPromise = null;

  // Client ID / SDK URL (sandbox vs live) come from the server so flipping
  // PAYPAL_ENV in Railway is the only change needed to go live — nothing to
  // re-upload here.
  function getConfig() {
    if (configPromise) return configPromise;
    configPromise = fetch(API_BASE + "/paypal-api/config").then(function (res) {
      return res.json();
    });
    return configPromise;
  }

  function loadPayPalSdk(sdkUrl) {
    return new Promise(function (resolve, reject) {
      if (window.paypal) return resolve(window.paypal);
      var script = document.createElement("script");
      script.src = sdkUrl;
      script.async = true;
      script.onload = function () {
        resolve(window.paypal);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function getSdkInstance() {
    if (sdkInstancePromise) return sdkInstancePromise;
    sdkInstancePromise = getConfig().then(function (config) {
      return loadPayPalSdk(config.sdkUrl).then(function (paypal) {
        return paypal.createInstance({
          clientId: config.clientId,
          components: ["paypal-guest-payments"],
        });
      });
    });
    return sdkInstancePromise;
  }

  function createDraftOrderFromCart() {
    return fetch("/cart.js")
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          throw new Error("Cart is empty");
        }
        // Carry over any discount (automatic or code-based) that Shopify's
        // own cart already applied to each line — otherwise the draft order
        // just uses the variant's full price and the customer gets charged
        // more than what the cart showed them.
        var line_items = cart.items.map(function (item) {
          var line = { variant_id: item.variant_id, quantity: item.quantity };
          if (item.total_discount && item.total_discount > 0) {
            line.applied_discount = {
              value_type: "fixed_amount",
              value: (item.total_discount / 100).toFixed(2),
              title:
                (item.discounts && item.discounts[0] && item.discounts[0].title) ||
                "Descuento",
            };
          }
          return line;
        });
        return fetch(API_BASE + "/create-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line_items: line_items }),
        });
      })
      .then(function (res) {
        return res.json();
      });
  }

  function createPayPalOrder(draftOrderId) {
    return fetch(API_BASE + "/paypal-api/checkout/orders/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ draftOrderId: draftOrderId }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        return { orderId: data.id };
      });
  }

  function captureOrder(orderId) {
    return fetch(API_BASE + "/paypal-api/checkout/orders/" + orderId + "/capture", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).then(function (res) {
      if (!res.ok) throw new Error("Capture request failed");
      return res.json();
    });
  }

  function buildOrderPromise() {
    return createDraftOrderFromCart().then(function (draft) {
      if (!draft.draftOrderId) throw new Error("No draft order created");
      return createPayPalOrder(draft.draftOrderId);
    });
  }

  // Instead of hiding PayPal's button, we hide Shopify's native checkout
  // button and put PayPal's own <paypal-basic-card-button> right in its
  // place — this matches the exact pattern that already worked on the
  // standalone test page (the SDK renders its inline card form / popup
  // relative to that real, visible button).
  function insertPayPalButton(nativeButton) {
    if (nativeButton.dataset.paypalReplaced) return;
    nativeButton.dataset.paypalReplaced = "true";
    nativeButton.style.display = "none";

    // PayPal's card form renders with a plain white/light background that
    // we can't restyle directly, so instead of leaving it floating on the
    // theme's dark cart background, we give it a light "card" of its own
    // (your brand's cream tone) so the contrast reads as intentional.
    var wrapper = document.createElement("div");
    wrapper.style.cssText =
      "background:#f6eddd;border-radius:10px;padding:16px;margin-top:12px;";

    var container = document.createElement("paypal-basic-card-container");
    container.style.cssText = "display:block;width:100%;";
    var button = document.createElement("paypal-basic-card-button");
    container.appendChild(button);
    wrapper.appendChild(container);
    nativeButton.insertAdjacentElement("afterend", wrapper);

    // Make sure the SDK is initialized now that the real button exists in
    // the DOM (it registers buttons present at createInstance() time).
    getSdkInstance();

    // Once the card is approved there are a couple of seconds where we're
    // still capturing the payment and completing the Shopify order — with
    // nothing on screen that gap reads as broken. Swap the card form for a
    // "processing" state in the same cream card so it looks intentional.
    var cardMarkup = wrapper.innerHTML;
    function showProcessing() {
      // Site is bilingual via an /en URL prefix (see the hero section's own
      // locale switching) — match that instead of hardcoding Spanish.
      var isEN = /^\/en(\/|$)/.test(window.location.pathname);
      var label = isEN ? "Processing your payment, one moment…" : "Procesando tu pago, un momento…";
      wrapper.innerHTML =
        '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:20px 0;">' +
        '<div style="width:32px;height:32px;border-radius:50%;border:3px solid rgba(32,34,25,0.15);border-top-color:#202219;animation:ptso-pay-spin 0.8s linear infinite;"></div>' +
        '<div style="font-family:Helvetica,Arial,sans-serif;font-size:15px;color:#202219;opacity:0.85;">' + label + '</div>' +
        "</div>";
    }
    function restoreCardForm() {
      wrapper.innerHTML = cardMarkup;
    }
    if (!document.getElementById("ptso-pay-spin-style")) {
      var spinStyle = document.createElement("style");
      spinStyle.id = "ptso-pay-spin-style";
      spinStyle.textContent =
        "@keyframes ptso-pay-spin { to { transform: rotate(360deg); } }";
      document.head.appendChild(spinStyle);
    }

    button.addEventListener("click", function () {
      var orderPromise = buildOrderPromise();
      getSdkInstance()
        .then(function (sdkInstance) {
          return sdkInstance.createPayPalGuestOneTimePaymentSession({
            onApprove: function (data) {
              showProcessing();
              captureOrder(data.orderId)
                .then(function () {
                  // Empty the cart now that the order was created from it —
                  // otherwise the old items are still sitting there if the
                  // customer comes back (or hits the browser back button).
                  return fetch("/cart/clear.js", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                  })
                    .then(function (res) {
                      if (!res.ok) console.error("Cart clear failed, status", res.status);
                    })
                    .catch(function (err) {
                      console.error("Cart clear request failed:", err);
                    });
                })
                .then(function () {
                  window.location.href = window.location.origin + "/pages/order-confirmed";
                })
                .catch(function (err) {
                  console.error("Capture failed:", err);
                  restoreCardForm();
                  alert(
                    "Hubo un problema confirmando tu pago. Si se descontó de tu tarjeta, contactanos."
                  );
                });
            },
            onCancel: function (data) {
              console.log("Payment cancelled", data);
            },
            onError: function (err) {
              console.error("PayPal payment error:", err);
              alert("Hubo un error procesando el pago. Probá de nuevo.");
            },
          });
        })
        .then(function (session) {
          return session.start(
            { presentationMode: "auto", targetElement: button },
            orderPromise
          );
        })
        .catch(function (err) {
          console.error("Checkout error:", err);
          alert("No pudimos iniciar el pago. Probá de nuevo en unos segundos.");
        });
    });
  }

  // Free orders ($0 total, e.g. a demo case) have nothing to charge — routing
  // those through the PayPal card form errors out (you can't charge a card
  // $0). Shopify's own native checkout already handles $0 orders correctly
  // ("Tu pedido es gratuito. No se requiere ningún pago."), so for those we
  // leave the native #checkout button alone instead of replacing it.
  function revertToNativeButton(nativeButton) {
    setFreeLabel(nativeButton);
    if (!nativeButton.dataset.paypalReplaced) return;
    delete nativeButton.dataset.paypalReplaced;
    nativeButton.style.display = "";
    var wrapper = nativeButton.nextElementSibling;
    if (wrapper && wrapper.querySelector && wrapper.querySelector("paypal-basic-card-container")) {
      wrapper.remove();
    }
  }

  // "Pagar" reads oddly on a $0 order — swap it for something neutral while
  // the cart total is free, and put the original label back once a paid
  // item is added (so the PayPal-replaced button underneath keeps its
  // normal Shopify text if it's ever shown again).
  function setFreeLabel(nativeButton) {
    if (!nativeButton.dataset.originalLabel) {
      nativeButton.dataset.originalLabel = nativeButton.textContent.trim();
    }
    nativeButton.textContent = "Continuar";
  }

  function restoreOriginalLabel(nativeButton) {
    if (nativeButton.dataset.originalLabel) {
      nativeButton.textContent = nativeButton.dataset.originalLabel;
    }
  }

  function attachToCheckoutButton() {
    var button = document.getElementById("checkout");
    if (!button) return;
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart.total_price === 0) {
          revertToNativeButton(button);
        } else {
          restoreOriginalLabel(button);
          insertPayPalButton(button);
        }
      })
      .catch(function () {
        // If we can't read the cart for some reason, default to the PayPal
        // flow rather than leaving a broken state.
        insertPayPalButton(button);
      });
  }

  // The cart drawer re-renders its contents dynamically (AJAX cart), so the
  // #checkout button can be replaced at any time — watch for it.
  document.addEventListener("DOMContentLoaded", attachToCheckoutButton);
  var observer = new MutationObserver(attachToCheckoutButton);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // --- Cart icon bump animation ---------------------------------------
  // Unrelated to PayPal, but this script is already loaded on every page,
  // so it's the simplest place to add a small "pulse" on the cart bubble
  // count whenever it changes (item added/removed).
  var bumpStyle = document.createElement("style");
  bumpStyle.textContent =
    "@keyframes ptso-cart-bump { 0% { transform: scale(1); } 35% { transform: scale(1.35); } 100% { transform: scale(1); } }" +
    ".ptso-cart-bump { animation: ptso-cart-bump 0.35s ease; }" +
    // The bubble number was the same muted tone as its background circle —
    // force white so the count is actually readable.
    ".cart-bubble__text-count { color: #fff !important; }" +
    // The "Inicia sesión" link in the empty-cart message has no class of
    // its own, so it falls back to the theme's default (bright) link
    // color, which clashes with the muted paragraph around it. `inherit`
    // lost to the theme's own link-color rule (higher specificity), so
    // set the paragraph's actual tan tone explicitly instead.
    'a[href*="/account/login"] { color: #a99d84 !important; opacity: 0.85; text-decoration: underline; }';
  document.head.appendChild(bumpStyle);

  function watchCartBubble() {
    var counts = document.querySelectorAll(".cart-bubble__text-count");
    counts.forEach(function (el) {
      if (el.dataset.ptsoBumpBound) return;
      el.dataset.ptsoBumpBound = "true";
      var lastValue = el.textContent;
      var mo = new MutationObserver(function () {
        if (el.textContent !== lastValue) {
          lastValue = el.textContent;
          el.classList.remove("ptso-cart-bump");
          // Force reflow so the animation can restart even if triggered again quickly.
          void el.offsetWidth;
          el.classList.add("ptso-cart-bump");
        }
      });
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }
  document.addEventListener("DOMContentLoaded", watchCartBubble);
  var bubbleObserver = new MutationObserver(watchCartBubble);
  bubbleObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
